import { BomCompleteness, BomMissingField } from '../types/bom.types';

/**
 * The rules every editable BOM sheet shares: what a valid quantity and price
 * look like, and which lines need the buyer's attention before discovery.
 * `assessCompleteness` mirrors buyer-service `services/bom/completeness.js`
 * line for line, so a sheet with unsaved edits counts exactly what the server
 * will count once they are saved.
 */

export interface BomLineValues {
  componentId?: string | null;
  name?: string | null;
  partNumber?: string | null;
  mpn?: string | null;
  description?: string | null;
  specifications?: string | null;
  quantity?: number | string | null;
}

const MISSING_ORDER: BomMissingField[] = ['name', 'quantity', 'partNumber', 'description'];

const hasText = (value: unknown): boolean => typeof value === 'string' && value.trim().length > 0;

const isWholeQuantity = (value: unknown): boolean =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1;

/** A typed quantity: a whole number of at least 1, else null. */
export const parseQuantity = (raw: string): number | null => {
  const text = raw.trim();
  if (text === '') return null;
  const n = Number(text);
  return isWholeQuantity(n) ? n : null;
};

/** A typed target price: a finite number of at least 0, else null. */
export const parsePrice = (raw: string): number | null => {
  const text = raw.trim();
  if (text === '') return null;
  const n = Number(text);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** What a line lacks, in the server's order. Empty when the line is complete. */
export const missingFor = (line: BomLineValues): BomMissingField[] => {
  const missing: BomMissingField[] = [];
  if (!hasText(line.name)) missing.push('name');
  if (!isWholeQuantity(line.quantity)) missing.push('quantity');
  if (!hasText(line.partNumber) && !hasText(line.mpn)) missing.push('partNumber');
  if (!hasText(line.description) && !hasText(line.specifications)) missing.push('description');
  return missing;
};

export const assessCompleteness = (lines: BomLineValues[]): BomCompleteness => ({
  total: lines.length,
  incomplete: lines
    .map((line) => ({ componentId: line.componentId ?? null, missing: missingFor(line) }))
    .filter((entry) => entry.missing.length > 0),
});

const LACKS: Record<BomMissingField, string> = {
  name: 'no name',
  quantity: 'no quantity',
  partNumber: 'no part number or MPN',
  description: 'no description',
};

/**
 * "3 lines need attention: 2 have no part number or MPN, 1 has no quantity" —
 * the most common gap first. One line reads "1 line needs attention: it has
 * no quantity and no description". Null when every line is complete.
 */
export const describeCompleteness = (completeness: BomCompleteness): string | null => {
  const { incomplete } = completeness;
  if (incomplete.length === 0) return null;

  if (incomplete.length === 1) {
    const gaps = MISSING_ORDER.filter((f) => incomplete[0].missing.includes(f)).map((f) => LACKS[f]);
    const listed = gaps.length > 1 ? `${gaps.slice(0, -1).join(', ')} and ${gaps[gaps.length - 1]}` : gaps[0];
    return `1 line needs attention: it has ${listed}`;
  }

  const counts = MISSING_ORDER.map((field) => ({
    field,
    count: incomplete.filter((entry) => entry.missing.includes(field)).length,
  }))
    .filter((c) => c.count > 0)
    // Array.prototype.sort is stable, so ties keep the server's field order.
    .sort((a, b) => b.count - a.count);

  const parts = counts.map(({ field, count }) => `${count} ${count === 1 ? 'has' : 'have'} ${LACKS[field]}`);
  return `${incomplete.length} lines need attention: ${parts.join(', ')}`;
};
